/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Divider, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import FilePicker from "components/FilePicker";
import React from "react";
import { getLogos, getUploadUrls } from "services/logoService";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useSnackbar } from "hooks/useSnackbar";
import { uploadImageV2 } from "services/uploadService";

const useStyles = makeStyles((theme) => ({
  imagePickerContainer: {
    width: "100%",
    display: "flex",
    flexFlow: "row wrap",
    alignItems: "end",
    gap: theme.spacing(2),
  },
  botghostBtnContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  uploadBtnContainer: {
    width: 250,
    maxWidth: 300,
    minWidth: 100,
  },
  pickerContainer: { display: "flex", flexDirection: "column", gap: 20 },
  pickerHeaderText: { fontSize: "18px", fontWeight: "bold" },
}));

const commonLogoDetails = {
  image: null,
  imageUrl: "",
  uploadUrl: "",
};

const Logos = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [logoDetails, setLogoDetails] = React.useState({
    color: {
      ...commonLogoDetails,
      label: "Color Logo",
    },
    white: {
      ...commonLogoDetails,
      label: "White Logo",
    },
  });

  const onSelectImage = (img, type) => {
    setLogoDetails((state) => ({
      ...state,
      [type]: {
        ...state[type],
        image: img,
      },
    }));
  };

  const fetchLogos = async ({ includeUploadUrls = true } = {}) => {
    setLoading(true);
    try {
      let imageUrlsRes;
      let uploadUrlsRes = { data: {} };

      if (includeUploadUrls) {
        [imageUrlsRes, uploadUrlsRes] = await Promise.all([
          getLogos(),
          getUploadUrls(),
        ]);
      } else {
        imageUrlsRes = await getLogos();
      }

      setLogoDetails((state) => {
        let updatedColorDetails = { ...state };

        Object.keys(imageUrlsRes.data || {}).forEach((key) => {
          updatedColorDetails[key] = {
            ...updatedColorDetails[key],
            imageUrl: imageUrlsRes.data[key] || "",
            uploadUrl: includeUploadUrls
              ? uploadUrlsRes.data[`${key}Url`]
              : state[key]?.uploadUrl,
          };
        });

        return updatedColorDetails;
      });
    } catch (error) {
      console.error("Error fetching logos:", error);
      showSnackbar("Failed to fetch logos", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    try {
      const promises = Object.keys(logoDetails)
        .filter((key) => logoDetails[key].image)
        .map((key) =>
          uploadImageV2({
            data: logoDetails[key].image,
            uploadUrl: logoDetails[key].uploadUrl,
            resize: true,
            resizeToWidth: 700,
            resizeToHeight: 760,
          })
        );

      const results = await Promise.allSettled(promises);

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.status) {
          showSnackbar(result.value.message ?? "Image Uploaded", "success");
        } else if (result.status === "rejected") {
          showSnackbar(
            result.reason?.message ?? "Image upload failed",
            "error"
          );
        }
      });
    } catch (error) {
      console.error(error);
      showSnackbar(error.message || "Unexpected error during upload");
    } finally {
      setLogoDetails((st) => {
        return {
          ...st,
          color: {
            ...st.color,
            ...commonLogoDetails,
            uploadUrl: st.color.uploadUrl,
          },
          white: {
            ...st.white,
            ...commonLogoDetails,
            uploadUrl: st.white.uploadUrl,
          },
        };
      });

      await fetchLogos({ includeUploadUrls: false });
    }
  };

  React.useEffect(() => {
    fetchLogos();
  }, []);

  return (
    <ContentShell
      title="Logos"
      loading={loading}
      actions={
        <CustomButton
          disabled={!(logoDetails.color.image || logoDetails.white.image)}
          startIcon={<CloudUploadIcon />}
          onClick={handleUpload}
        >
          Upload
        </CustomButton>
      }
    >
      <Box className={styles.imagePickerContainer}>
        {["color", "white"].map((type) => {
          const _details = logoDetails[type];
          const _style = { width: "50%", marginTop: 20 };
          if (type === "white") {
            _style["backgroundColor"] = "black";
            _style["border-style"] = "solid";
            _style["padding"] = "5px";
          }

          return (
            <Box className={styles.pickerContainer} key={type}>
              <Typography
                className={styles.pickerHeaderText}
              >{`Select ${type} Image`}</Typography>
              <FilePicker
                value={_details.image}
                name={type}
                onFileSelect={(img) => onSelectImage(img, type)}
                metaData={{
                  // height: 760,
                  // width: 700,
                  note: "File dimension should be less than or equal to 700 x 760 pixels. You can upload higher res, but it will be resized in above res!",
                }}
                selectedImgPreviewStyle={
                  type === "white" ? { backgroundColor: "black" } : null
                }
              />
              {type === "white" && (
                <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                  Note: Previewed with black background so that image is fully
                  visible
                </Typography>
              )}
              {_details.imageUrl && !_details.image && (
                <Box
                  component="img"
                  src={_details?.imageUrl}
                  alt="Image Preview"
                  className={(styles as any).previewBox}
                  style={_style}
                />
              )}
              <Divider variant="fullWidth" style={{ width: "100%" }} />
            </Box>
          );
        })}
      </Box>
    </ContentShell>
  );
};

export default Logos;
